import { useEffect } from "react";
import Lenis from "lenis";
import { gsap, ScrollTrigger, prefersReducedMotion } from "../lib/gsap";

/**
 * Module-level handle to the active Lenis instance, so components outside
 * the root (e.g. <Nav>) can trigger a smooth scroll-to-section without
 * prop-drilling. `null` when Lenis isn't running (e.g. reduced-motion).
 */
let activeLenis: Lenis | null = null;

export function getLenis(): Lenis | null {
  return activeLenis;
}

/**
 * Initializes Lenis smooth scroll and wires it to GSAP ScrollTrigger.
 *
 * CRITICAL INTEGRATION:
 *  - Lenis hijacks native scroll, so ScrollTrigger must update on every Lenis
 *    scroll event, and Lenis must be driven by gsap.ticker (single RAF loop).
 *  - Without this, pinned/scrubbed animations desync and jitter.
 *  - Respects prefers-reduced-motion by skipping smooth scroll entirely.
 *
 * Because Lenis intercepts wheel/touch scrolling, plain `<a href="#faq">`
 * anchor jumps do NOT reliably work while Lenis is active — the browser's
 * native anchor-scroll can race with or be overridden by Lenis. Use
 * `getLenis()?.scrollTo(...)` (see `lib/scroll-to-section.ts`) instead of
 * relying on native anchor navigation for in-page section links.
 */
export function useLenis() {
  useEffect(() => {
    if (prefersReducedMotion()) return;

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });
    activeLenis = lenis;

    // 1) Update ScrollTrigger whenever Lenis scrolls.
    lenis.on("scroll", ScrollTrigger.update);

    // 2) Drive Lenis from GSAP's ticker (single RAF loop, no double-RAF drift).
    const raf = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      lenis.destroy();
      activeLenis = null;
    };
  }, []);
}
